import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react'
import { createPortal } from 'react-dom'
import { Alert } from './Alert'
import AlertContext from './Context'
import { AlertDescription, AlertProviderProps, AlertType } from './types'


const AlertProvider = ({ children, }: AlertProviderProps) => {
    const root = useRef<HTMLDivElement | null>(null);
    const [alerts, setAlerts] = useState<AlertDescription[]>([]);

    useEffect(() => {
        root.current = document.createElement('div');
        root.current.id = '__react-alert__';
        document.body.appendChild(root.current);

        return () => {
            if (root.current) document.body.removeChild(root.current)
        }
    }, []);

    const remove = useCallback((alert: AlertDescription) => {
        setAlerts(currentAlerts => {
            const filteredAlerts = currentAlerts.filter(a => a.id !== alert.id);
            return filteredAlerts;
        })
    }, [setAlerts]);

    const show = useCallback((message = '', title = '', type: AlertType = AlertType.ERROR) => {
        const id = Math.random()
            .toString(36)
            .substr(2, 9)

        const alert: AlertDescription = {
            id,
            message,
            title,
            type,
        }

        setAlerts(state => state.concat(alert));

        return alert;
    }, [])

    const success = useCallback((message = '', title = '') => {
        return show(message, title, AlertType.SUCCESS)
    }, [show])

    const error = useCallback((message = '', title = '') => {
        return show(message, title, AlertType.ERROR);
    }, [show])

    const info = useCallback((message = '', title = '') => {
        return show(message, title, AlertType.INFO)
    }, [show])

    const value = { alerts, show, error, success, info }

    return (
        <AlertContext.Provider value={value} >
            {children}
            {root.current && createPortal(
                <>
                    {alerts.map((alert) => (
                        <Alert
                            key={alert.id}
                            id={alert.id}
                            title={alert.title}
                            message={alert.message}
                            type={alert.type}
                            onClose={() => remove(alert)}
                        />
                    ))}
                </>,
                root.current
            )}
        </AlertContext.Provider>
    )
}

export default AlertProvider